import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactElement, ReactNode } from 'react';
import type { LoginRequest, Me } from '@zenda/api-contract';
import { clearToken, fetchMe, login as loginRequest, setToken, tokenVencido } from './api';

const ME_KEY = 'zenda.me';

/**
 * Uma sessão, uma operação. A atendente atende vários médicos e clínicas, mas eles são
 * CLIENTES dentro do tenant dela — não contas para alternar. Se ela precisasse trocar de
 * conta para ver as conversas, teria de volta as três telas que o Zenda existe para unir.
 */
interface AuthState {
  readonly me: Me | null;
  readonly signIn: (credentials: LoginRequest) => Promise<void>;
  readonly signOut: () => void;
  readonly adotarToken: (token: string) => Promise<void>;
  readonly recarregarMe: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

/**
 * A sessão que o navegador guardou, quando ela ainda pode valer.
 *
 * O `localStorage` não é autoridade sobre nada: ele diz o que o último login disse, e o
 * servidor é quem decide em cada chamada. Duas checagens antes de subir como logado, e
 * cada uma evita um sintoma diferente:
 *
 * - **prazo do token** — sem isso o app monta o painel inteiro com o nome da atendente
 *   e some no primeiro 401, que redireciona a página. Quem viu isso viu o produto
 *   piscar, não expirar.
 * - **o `Me` guardado existir e ser legível** — token sem retrato não monta tela.
 *
 * O que ela NÃO decide: se a sessão vale de verdade. Isso é `/api/me`, logo abaixo.
 */
function readStoredMe(): Me | null {
  if (tokenVencido()) return null;
  const raw = window.localStorage.getItem(ME_KEY);
  if (raw === null) return null;
  try {
    return JSON.parse(raw) as Me;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }): ReactElement {
  const [me, setMe] = useState<Me | null>(readStoredMe);
  const conferido = useRef(false);

  /**
   * Confere no servidor a sessão que veio do `localStorage`, sem segurar a tela.
   *
   * Token dentro do prazo não garante que o usuário continue existindo, que a
   * membership dele continue ativa, nem que o nome da operação seja o mesmo. `/api/me`
   * é quem sabe: 401 cai no tratamento único do cliente de API, que limpa o token e
   * volta ao login; e a resposta boa atualiza o retrato guardado.
   *
   * Não segura a primeira pintura de propósito: o retrato guardado é bom o bastante
   * para desenhar, e uma espera aqui atrasaria toda abertura de aba por uma checagem
   * que quase sempre confirma.
   */
  useEffect(() => {
    if (conferido.current || me === null) return;
    conferido.current = true;
    void fetchMe()
      .then((atual) => {
        window.localStorage.setItem(ME_KEY, JSON.stringify(atual));
        setMe(atual);
      })
      // Falha de rede mantém o retrato: derrubar a sessão porque o wi-fi caiu tiraria
      // a atendente da tela no meio de um atendimento.
      .catch(() => undefined);
  }, [me]);

  const signIn = useCallback(async (credentials: LoginRequest) => {
    const response = await loginRequest(credentials);
    setToken(response.token);
    window.localStorage.setItem(ME_KEY, JSON.stringify(response.me));
    // O login acabou de trazer o retrato do servidor: conferir de novo seria uma
    // chamada para saber o que se acabou de saber.
    conferido.current = true;
    setMe(response.me);
  }, []);

  const signOut = useCallback(() => {
    clearToken();
    window.localStorage.removeItem(ME_KEY);
    conferido.current = false;
    setMe(null);
  }, []);

  const adotarToken = useCallback(async (token: string) => {
    setToken(token);
    const atual = await fetchMe();
    window.localStorage.setItem(ME_KEY, JSON.stringify(atual));
    setMe(atual);
  }, []);

  /**
   * Busca o retrato de novo: entrar numa equipe, sair, trocar o nome. O `Me` carrega as
   * equipes e os convites pendentes, e a tela inteira lê dele.
   */
  const recarregarMe = useCallback(async () => {
    const atual = await fetchMe().catch(() => null);
    if (atual === null) return;
    window.localStorage.setItem(ME_KEY, JSON.stringify(atual));
    setMe(atual);
  }, []);

  const value = useMemo<AuthState>(
    () => ({ me, signIn, signOut, adotarToken, recarregarMe }),
    [me, signIn, signOut, adotarToken, recarregarMe],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (ctx === null) throw new Error('useAuth precisa estar dentro de <AuthProvider>');
  return ctx;
}
